/**
 * Reservations and amenities from the copy (#561), shaped as the list
 * endpoints return them. The engine sends each table's own columns; what
 * REST joins in is joined here from the other synced tables, so a renamed
 * amenity or an edited address shows everywhere at once.
 *
 * What the copy does not hold — a booking's access code, internal notes,
 * cancel outcome, check-in window and outstanding documents, an amenity's
 * photos, agreement, price rules and document requirements — is read by
 * the detail pages from REST, as before.
 */
import { useMemo } from 'react';
import {
  AmenityClosureReturn,
  AmenityReturn,
  HomeReturn,
  ReservationReturn,
} from '../clients/rust-api-client';
import { useSyncReady, useTable, values } from './hooks';

/** "12 Oak St Unit 4", as the reservation endpoints write a home. */
const addressOf = (home: HomeReturn): string => {
  const s2 = home.street2?.trim();
  return s2 ? `${home.street} ${home.street2}` : home.street;
};

export interface ReservationFilter {
  hoaId?: string | null;
  homeId?: string | null;
  amenityId?: string | null;
  /** Any of these; all when empty or absent. */
  statuses?: string[];
  /** Inclusive, on the booking's local date (`YYYY-MM-DD`), as the API. */
  from?: string | null;
  to?: string | null;
  newestFirst?: boolean;
}

/**
 * Bookings this viewer may see, filtered and ordered as
 * `GET /reservations` does: by start, soonest first unless
 * `newestFirst`, then by id. `null` without a copy: fetch.
 */
export function useSyncedReservations(filter: ReservationFilter): ReservationReturn[] | null {
  const ready = useSyncReady();
  const reservations = useTable('reservation');
  const amenities = useTable('amenity');
  const homes = useTable('home');
  const { hoaId, homeId, amenityId, from, to, newestFirst } = filter;
  const statusKey = (filter.statuses ?? []).join(',');
  return useMemo(() => {
    if (!ready) return null;
    const statuses = statusKey ? new Set(statusKey.split(',')) : null;
    const rows = values(reservations)
      .filter(
        (r) =>
          (!hoaId || r.hoa_id === hoaId) &&
          (!homeId || r.home_id === homeId) &&
          (!amenityId || r.amenity_id === amenityId) &&
          (!statuses || statuses.has(r.status)) &&
          (!from || r.local_date >= from) &&
          (!to || r.local_date <= to)
      )
      .map((r) => {
        const home = homes.get(r.home_id);
        return {
          ...r,
          amenity_name: amenities.get(r.amenity_id)?.name ?? r.amenity_name,
          home_address: home ? addressOf(home) : r.home_address,
        };
      });
    const dir = newestFirst ? -1 : 1;
    return rows.sort(
      (a, b) => dir * a.start_local.localeCompare(b.start_local) || a.id.localeCompare(b.id)
    );
  }, [ready, reservations, amenities, homes, hoaId, homeId, amenityId, statusKey, from, to, newestFirst]);
}

/**
 * One booking, as the list endpoint shapes it: `undefined` when the copy
 * does not hold it, `null` without a copy. The fields the copy never
 * carries (access code, internal notes, cancel outcome, check-in window,
 * documents) are absent; the detail page reads them from REST.
 */
export function useSyncedReservation(
  id: string | undefined
): ReservationReturn | null | undefined {
  const ready = useSyncReady();
  const reservations = useTable('reservation');
  const amenities = useTable('amenity');
  const homes = useTable('home');
  return useMemo(() => {
    if (!ready) return null;
    const r = id ? reservations.get(id) : undefined;
    if (!r) return undefined;
    const home = homes.get(r.home_id);
    return {
      ...r,
      amenity_name: amenities.get(r.amenity_id)?.name ?? r.amenity_name,
      home_address: home ? addressOf(home) : r.home_address,
    };
  }, [ready, reservations, amenities, homes, id]);
}

/**
 * One amenity with its hours, seasons and group name: `undefined` when
 * the copy does not hold it (archived amenities included), `null` without
 * a copy.
 */
export function useSyncedAmenity(
  id: string | undefined,
  hoaId: string | undefined
): AmenityReturn | null | undefined {
  const all = useSyncedAmenities({ hoaId, includeInactive: true, includeArchived: true });
  return useMemo(() => (all === null ? null : all.find((a) => a.id === id)), [all, id]);
}

/** How many requests wait on a manager — the sidebar's badge. */
export function useSyncedPendingReservationCount(): number | null {
  const ready = useSyncReady();
  const reservations = useTable('reservation');
  return useMemo(
    () => (ready ? values(reservations).filter((r) => r.status === 'pending').length : null),
    [ready, reservations]
  );
}

/**
 * An association's amenities as `GET /amenities` lists them: active and
 * unarchived unless asked, by sort order then name, with their hours,
 * seasons and group name. `null` without a copy.
 */
export function useSyncedAmenities(filter: {
  hoaId?: string | null;
  includeInactive?: boolean;
  includeArchived?: boolean;
}): AmenityReturn[] | null {
  const ready = useSyncReady();
  const amenities = useTable('amenity');
  const hours = useTable('amenityHours');
  const seasons = useTable('amenitySeason');
  const groups = useTable('amenityGroup');
  const hoas = useTable('hoa');
  const { hoaId, includeInactive = false, includeArchived = false } = filter;
  return useMemo(() => {
    if (!ready) return null;
    const hoursOf = new Map<string, AmenityReturn['hours']>();
    values(hours)
      .slice()
      .sort((a, b) => a.weekday - b.weekday || a.open_minute - b.open_minute)
      .forEach((h) => {
        const list = hoursOf.get(h.amenity_id) ?? [];
        list.push({
          weekday: h.weekday,
          open_minute: h.open_minute,
          close_minute: h.close_minute,
          ...(h.season_id ? { season_id: h.season_id } : {}),
        });
        hoursOf.set(h.amenity_id, list);
      });
    const seasonsOf = new Map<string, AmenityReturn['seasons']>();
    values(seasons)
      .slice()
      .sort((a, b) => a.start_month - b.start_month || a.start_day - b.start_day)
      .forEach((s) => {
        const list = seasonsOf.get(s.amenity_id) ?? [];
        list.push(s);
        seasonsOf.set(s.amenity_id, list);
      });
    return values(amenities)
      .filter(
        (a) =>
          (!hoaId || a.hoa_id === hoaId) &&
          (includeInactive || a.is_active) &&
          (includeArchived || a.archived_at == null)
      )
      .map((a) => ({
        ...a,
        // The association's zone, from its own synced row: setting it
        // (#661) changes the HOA, not its amenities, so the zone the
        // engine stamped on the amenity would go stale.
        timezone: hoas.get(a.hoa_id)?.timezone || a.timezone,
        hours: hoursOf.get(a.id) ?? [],
        seasons: seasonsOf.get(a.id) ?? [],
        group_name: a.group_id ? groups.get(a.group_id)?.name ?? null : null,
      }))
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }, [ready, amenities, hours, seasons, groups, hoas, hoaId, includeInactive, includeArchived]);
}

/**
 * An amenity's closures overlapping `[from, to]` (local dates), newest
 * first, as `GET /amenities/{id}/closures` does. `null` without a copy.
 */
export function useSyncedAmenityClosures(filter: {
  amenityIds: string[];
  from?: string | null;
  to?: string | null;
}): Record<string, AmenityClosureReturn[]> | null {
  const ready = useSyncReady();
  const closures = useTable('amenityClosure');
  const { from, to } = filter;
  const idKey = filter.amenityIds.join(',');
  return useMemo(() => {
    if (!ready) return null;
    const ids = new Set(idKey ? idKey.split(',') : []);
    const out: Record<string, AmenityClosureReturn[]> = {};
    ids.forEach((id) => {
      out[id] = [];
    });
    values(closures)
      .filter(
        (c) =>
          ids.has(c.amenity_id) &&
          // Ends after the first day starts, starts before the day after
          // the last ends — the API's overlap test.
          (!from || c.ends_local > `${from}T00:00:00`) &&
          (!to || c.starts_local.slice(0, 10) <= to)
      )
      .sort((a, b) => b.starts_local.localeCompare(a.starts_local))
      .forEach((c) => out[c.amenity_id].push(c));
    return out;
  }, [ready, closures, idKey, from, to]);
}
